import { useState } from 'react'
import { Toaster, toast } from 'sonner'
import Footer from './components/Footer'
import PaymentForm from './components/PaymentForm'
import PixPaymentScreen from './components/PixPaymentScreen'
import SuccessMessage from './components/SuccessMessage'
import { appContext } from './contexts/appContext'
import { AppContext, IFormData } from './types/app-context'
import {
  FormErrors,
  PaymentData,
  PaymentMethod,
  PaymentResponse,
} from './types/payment'
import { createPayment } from './utils/paymentApi'
import { getUrlParameter } from './utils/urlParams'
import { getValidationError } from './utils/validation'

import logoInfinity from './assets/images/logo_infinity.jpg'

const getExternalReference = (formData: IFormData) => {
  if (formData.IdReservaCurto && formData.IdPagamento) {
    return `urbania,${formData.IdReservaCurto},${formData.IdPagamento}`
  }

  return 'CHKOUT-' + Date.now()
}

function App() {
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [showPixScreen, setShowPixScreen] = useState(false)
  const [pixPaymentData, setPixPaymentData] = useState<PaymentResponse | null>(
    null
  )
  const [paymentValue, setPaymentValue] = useState<number>(0)
  const [formData, setFormData] = useState<IFormData>({})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('pix')
  const [errors, setErrors] = useState<FormErrors>({})
  const [paymentResponse, setPaymentResponse] =
    useState<PaymentResponse | null>(null)

  const handlePaymentSuccess = (response: PaymentResponse, value?: number) => {
    if (response.qrCode || response.pixCode) {
      // PIX payment - show dedicated screen
      setPixPaymentData(response)
      if (value) setPaymentValue(value)
      setShowPixScreen(true)
    } else {
      // Other payment methods - show success message
      setPaymentSuccess(true)
      setSuccessMessage(
        'Obrigado por sua compra. Você receberá um e-mail com os detalhes da transação.'
      )
    }
  }

  const handlePaymentError = (error: string) => {
    setPaymentError(error)
    setPaymentResponse(null)

    // Show user-friendly error message
    let userMessage =
      'Ocorreu um erro inesperado ao processar seu pagamento. Por favor, tente novamente mais tarde.'

    // Check for specific error types to provide better messages
    if (error.includes('network') || error.includes('fetch')) {
      userMessage = 'Erro de conexão. Verifique sua internet e tente novamente.'
    } else if (error.includes('timeout')) {
      userMessage = 'O pagamento demorou muito para processar. Tente novamente.'
    } else if (error.includes('validation') || error.includes('invalid')) {
      userMessage =
        'Dados inválidos. Verifique as informações e tente novamente.'
    } else if (error.includes('Transação não autorizada')) {
      userMessage = error
    }

    toast.error(userMessage)
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    let isValid = true

    const fieldsToValidate = [
      'payment-value',
      'customer-name',
      'customer-document',
    ]

    if (selectedMethod === 'boleto' || selectedMethod === 'credit-card') {
      fieldsToValidate.push(
        'customer-email',
        'customer-phone',
        'customer-zipcode',
        'customer-address',
        'customer-number',
        'customer-neighborhood',
        'customer-city',
        'customer-state'
      )
    } else if (selectedMethod === 'pix') {
      fieldsToValidate.push('customer-email', 'customer-phone')
    }

    if (selectedMethod === 'credit-card') {
      fieldsToValidate.push(
        'card-number',
        'card-expiry',
        'card-cvv',
        'card-holder'
      )
    }

    fieldsToValidate.forEach((field) => {
      const value = formData[field] || ''
      const error = getValidationError(field, value)
      if (error) {
        newErrors[field] = error
        isValid = false
      }
    })

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)

    try {
      const paymentData: PaymentData = {
        value: parseFloat(formData['payment-value'].replace(',', '.')),
        description: 'Pagamento Checkout',
        externalReference: getExternalReference(formData),
        billingType:
          selectedMethod === 'pix'
            ? 'PIX'
            : selectedMethod === 'boleto'
            ? 'BOLETO'
            : 'CREDIT_CARD',
        customer: {
          customerName: formData['customer-name'],
          customerEmail: formData['customer-email'],
          customerCpfCnpj:
            formData['customer-document']?.replace(/\D/g, '') || '',
          customerPhone: formData['customer-phone']?.replace(/\D/g, '') || '',
          customerPostalCode:
            formData['customer-zipcode']?.replace(/\D/g, '') || '',
          customerAddress: formData['customer-address'] || '',
          customerAddressNumber: formData['customer-number'] || '',
          customerComplement: formData['customer-complement'] || '',
          customerProvince: formData['customer-neighborhood'] || '',
          customerCityName: formData['customer-city'] || '',
          customerStateUF: formData['customer-state'] || '',
        },
      }

      if (selectedMethod === 'credit-card') {
        paymentData.creditCard = {
          holderName: formData['card-holder'],
          number: formData['card-number'].replace(/\D/g, ''),
          expiryMonth: formData['card-expiry'].split('/')[0],
          expiryYear: '20' + formData['card-expiry'].split('/')[1],
          ccv: formData['card-cvv'],
        }
        paymentData.installmentCount = parseInt(
          formData['card-installments'] || '1'
        )
      }

      const companyId = getUrlParameter('Id')
      if (companyId) {
        paymentData.Id = companyId
      }

      const response = await createPayment(paymentData)

      setPaymentResponse(response)

      if (selectedMethod === 'pix') {
        const paymentValue = parseFloat(
          formData['payment-value'].replace(',', '.')
        )
        // PIX will be handled by the dedicated screen
        handlePaymentSuccess(response, paymentValue)
      } else if (selectedMethod === 'credit-card') {
        if (response?.body?.errors && response.body.errors.length > 0) {
          handlePaymentError(response.body.errors[0].description)
        } else {
          handlePaymentSuccess(response)
        }
      }
    } catch (error) {
      console.error('Erro ao processar pagamento:', error)
      handlePaymentError(
        error instanceof Error ? error.message : 'Erro ao processar pagamento'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handlePixSuccess = () => {
    setShowPixScreen(false)
    setPixPaymentData(null)
    setPaymentSuccess(true)
    setSuccessMessage(
      'Pagamento PIX recebido com sucesso! Obrigado por sua compra.'
    )
  }

  const handlePixBack = () => {
    setShowPixScreen(false)
    setPixPaymentData(null)
    setPaymentResponse(null)
    // Não limpar o valor do pagamento ao voltar da tela PIX
  }

  const contextValue: AppContext = {
    paymentSuccess,
    paymentError,
    successMessage,
    showPixScreen,
    pixPaymentData,
    paymentValue,
    formData,
    isLoading,
    selectedMethod,
    errors,
    paymentResponse,

    setPaymentSuccess,
    setPaymentError,
    setSuccessMessage,
    setShowPixScreen,
    setPixPaymentData,
    setPaymentValue,
    handlePixSuccess,
    handlePixBack,
    handleSubmit,
    setFormData,
    setIsLoading,
    setSelectedMethod,
    setErrors,
    setPaymentResponse,
  }

  return (
    <appContext.Provider value={contextValue}>
      {showPixScreen ? (
        <PixPaymentScreen />
      ) : (
        <div className="min-h-screen bg-gray-50 py-2">
          <div className="container-compact">
            {paymentSuccess ? (
              <SuccessMessage message={successMessage} />
            ) : (
              <>
                <div className="header-compact flex flex-col items-center">
                  <img
                    src={logoInfinity}
                    alt="Logo infinity"
                    className="w-40"
                  />
                  <h1>Checkout</h1>
                  <p className="subtitle">Complete os dados para finalizar</p>
                  <p
                    style={{
                      color: 'var(--danger-color)',
                      fontSize: '11px',
                      marginTop: '4px',
                    }}
                  >
                    <i className="fas fa-info-circle"></i>
                    Campos marcados com * são obrigatórios
                  </p>
                </div>
                <PaymentForm />
              </>
            )}
          </div>

          <div className="mt-2">
            <Footer />
          </div>
        </div>
      )}
      <Toaster />
    </appContext.Provider>
  )
}

export default App
